import { BaseNode, TreeAdapter } from '@abp/ng.components/tree';
import { ListService, PagedResultDto } from '@abp/ng.core';
import { ConfirmationService } from '@abp/ng.theme.shared';
import { FormPropData, generateFormFromProps } from '@abp/ng.components/extensible';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, inject, Injector, Input, OnDestroy, OnInit, Output, signal, TrackByFunction } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  CostCentreTreeNode,
  mapCostCentresTreeNode,
} from '@/cost-centre/models/cost-centre-tree-node.model';
import { CostCentreDto, CostCentreService } from '@proxy/register-service/cost-centre';
import { StaffDto } from '@proxy/servicing-service/staffs';
import { StaffService } from '@proxy/servicing-service/staffs/staff.service';
import { LocationDto } from '@proxy/register-service/locations';
import { TenancyService } from '@proxy/administration-service/tenancy';
import { avatar } from '@/shared/constants';
import { of, Subject } from 'rxjs';
import { catchError, finalize, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import {
  IdentityRoleDto,
  IdentityUserDto,
  IdentityUserService,
  OrganizationUnitDto,
  OrganizationUnitWithDetailsDto,
} from '@volo/abp.ng.identity/proxy';
import { passwordComplexityValidator } from './helpers/password-complexity.validator';

type UserOrganizationState = {
  response: PagedResultDto<OrganizationUnitWithDetailsDto>;
  nodes: BaseNode[];
  checkedKeys: string[];
  expandedKeys: string[];
  selectFn: () => boolean;
};

@Component({
    selector: 'app-user-modal',
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './user-modal.component.html',
    styleUrls: ['./user-modal.scss'],
    providers: [ListService],
    standalone: false
})
export class UserModalComponent implements OnInit, OnDestroy {
  // State below is assigned from async callbacks; ABP's OnPush layout means the view
  // is only re-checked if it is explicitly marked.
  private readonly cdr = inject(ChangeDetectorRef);

  @Input() busy = false;
  @Input() selected = {} as IdentityUserDto;
  @Input() title = '';
  @Input() visible = false;

  @Output() saveClicked = new EventEmitter<Record<string, unknown>>();
  @Output() visibleChange = new EventEmitter<boolean>();

  form: FormGroup | null = null;
  roles: Array<IdentityRoleDto & { name: string }> = [];
  selectedRole = '';
  selectedUserRoles: IdentityRoleDto[] = [];
  selectedOrganizationUnits: OrganizationUnitDto[] = [];

  organization: UserOrganizationState = {
    response: {} as PagedResultDto<OrganizationUnitWithDetailsDto>,
    nodes: [],
    checkedKeys: [],
    expandedKeys: [],
    selectFn: () => false,
  };

  costCentres: PagedResultDto<CostCentreTreeNode> = { items: [], totalCount: 0 };
  costCentresLoading = false;
  checkedKeys: string[] = [];
  restrictedKeys: string[] = [];
  allowedKeys: string[] = [];
  restrictedEvents: string[] = [];
  selectedLocation: LocationDto | null = null;
  staffUser?: StaffDto;
  avatarUrl: string = avatar;
  readonly showPassword = signal(false);

  defaultNotificationSettings = {
    isEmail: false,
    isSms: false,
    isWhatsApp: false,
    isInApp: false,
  };
  notificationSettings = this.defaultNotificationSettings;
  notificationOptions = Object.keys(this.notificationSettings);

  pin = '';
  keyringPassword = '';
  qrImageSrc: string | ArrayBuffer = '';

  private _isCostCentre = false;

  get isCostCentre(): boolean {
    return this._isCostCentre;
  }

  set isCostCentre(value: boolean) {
    if (value && !this.costCentres.totalCount) {
      this.getCostCentreData();
    }
    if (!value) this.checkedKeys = this.selected?.extraProperties?.costCentres || [];

    this._isCostCentre = value;
  }

  trackByFn: TrackByFunction<AbstractControl> = (index, item) =>
    Object.keys(item.value)[0] || index;

  get isPinValid(): boolean {
    if (this.pin === '') {
      return !!this.selected?.id;
    }

    return /^\d{4}$/.test(this.pin);
  }

  private static readonly fieldLabels: Record<string, string> = {
    userName: 'Username',
    password: 'Password',
    name: 'Name',
    surname: 'Surname',
    email: 'Email',
    phoneNumber: 'Phone number',
    roleNames: 'Roles',
  };

  get isManager(): boolean {
    return this.roleGroups.some(group => group.value.manager === true);
  }

  get roleGroups(): FormGroup[] {
    if (!this.form) {
      return [];
    }

    return ((this.form.get('roleNames') as FormArray)?.controls as FormGroup[]) || [];
  }

  private readonly destroy$ = new Subject<void>();

  private readonly roleSelectionRequired = (control: AbstractControl) => {
    const roleArray = control as FormArray;
    const hasSelectedRole = roleArray.controls.some(roleGroup => {
      const groupValue = roleGroup.value as Record<string, boolean>;
      const roleName = Object.keys(groupValue)[0];

      return !!roleName && !!groupValue[roleName];
    });

    return hasSelectedRole ? null : { required: true };
  };

  constructor(
    private readonly tenancyService: TenancyService,
    private readonly staffService: StaffService,
    private readonly costService: CostCentreService,
    private readonly identityUserService: IdentityUserService,
    private readonly confirmationService: ConfirmationService,
    private readonly fb: FormBuilder,
    private readonly injector: Injector,
  ) {}

  ngOnInit() {
    const userId = this.selected?.id;

    if (!userId) {
      this.generatePin();
      this.generateKeyringPassword();
      this.buildForm();
      return;
    }

    this.seedFromSelected();

    this.identityUserService
      .getRoles(userId)
      .pipe(
        tap(res => (this.selectedUserRoles = res.items || [])),
        switchMap(() => this.identityUserService.getOrganizationUnits(userId)),
        tap(res => (this.selectedOrganizationUnits = res)),
        take(1),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.buildForm();
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private seedFromSelected() {
    const extraProperties = this.selected.extraProperties;

    if (extraProperties?.restrictedLocations) {
      this.restrictedKeys = extraProperties.restrictedLocations;
    }
    if (extraProperties?.allowedLocations) {
      this.allowedKeys = extraProperties.allowedLocations;
    }
    if (extraProperties?.notificationSettings) {
      this.notificationSettings = extraProperties.notificationSettings;
    }
    if (extraProperties?.restrictedEvents) {
      this.restrictedEvents = extraProperties.restrictedEvents;
    }
    this.isCostCentre = !!extraProperties?.costCentres?.length;
    if (extraProperties?.costCentres) {
      this.checkedKeys = extraProperties.costCentres;
    }
  }

  private buildForm() {
    const data = new FormPropData(this.injector, this.selected);
    this.form = generateFormFromProps(data);
    this.form.get('extraProperties.pin')?.clearValidators();
    this.form.get('extraProperties.pin')?.updateValueAndValidity({ emitEvent: false });
    this.form.get('extraProperties.keyringPassword')?.clearValidators();
    this.form.get('extraProperties.keyringPassword')?.updateValueAndValidity({ emitEvent: false });

    this.form.get('name')?.addValidators(Validators.required);
    this.form.get('surname')?.addValidators(Validators.required);
    this.form.get('name')?.updateValueAndValidity({ emitEvent: false });
    this.form.get('surname')?.updateValueAndValidity({ emitEvent: false });
    this.form.get('password')?.addValidators([Validators.minLength(6), passwordComplexityValidator]);
    this.form.get('password')?.updateValueAndValidity({ emitEvent: false });

    this.identityUserService
      .getAssignableRoles()
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ items }) => {
        this.roles = (items ?? []).filter(
          (role): role is IdentityRoleDto & { name: string } => !!role.name,
        );
        this.cdr.markForCheck();
        const roleNamesArray = this.fb.array(
          this.roles.map(role => {
            const isSelected = this.selected.id
              ? !!this.selectedUserRoles?.find(userRole => userRole.id === role.id)
              : role.isDefault;

            return this.fb.group({
              [role.name]: [isSelected],
            });
          }),
          { validators: [this.roleSelectionRequired] },
        );

        const selectedRoleGroup = roleNamesArray.controls.find(roleGroup => {
          const groupValue = roleGroup.value as Record<string, boolean>;
          const roleName = Object.keys(groupValue)[0];

          return !!roleName && !!groupValue[roleName];
        });

        this.selectedRole = selectedRoleGroup
          ? Object.keys((selectedRoleGroup.value as Record<string, boolean>) ?? {})[0] || ''
          : '';
        this.cdr.markForCheck();

        this.form?.addControl('roleNames', roleNamesArray);
      });

    if (this.selected?.id) {
      this.getStaff(this.selected.id);
    }

    this.identityUserService
      .getAvailableOrganizationUnits()
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        const organizationItems = res.items ?? [];

        this.organization.response = res;
        this.organization.nodes = new TreeAdapter(
          organizationItems as BaseNode[],
        ).getTree() as BaseNode[];
        this.organization.expandedKeys = organizationItems
          .map(item => item.id)
          .filter((id): id is string => !!id);
        this.organization.checkedKeys = this.selectedOrganizationUnits
          .map(unit => unit.id)
          .filter((id): id is string => !!id);
        this.cdr.markForCheck();
      });
  }

  private getCostCentreData() {
    this.costCentresLoading = true;
    this.costService
      .getList({ skipCount: 0, maxResultCount: 100 })
      .pipe(
        finalize(() => {
          this.costCentresLoading = false;
          this.cdr.markForCheck();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (list: PagedResultDto<CostCentreDto>) => {
          this.costCentres = new PagedResultDto<CostCentreTreeNode>({
            totalCount: list.totalCount,
            items: mapCostCentresTreeNode(list.items ?? []),
          });
          this.cdr.markForCheck();
        },
      });
  }

  private getStaff(userId: string) {
    this.staffService
      .getList({ userId, maxResultCount: 1 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: data => {
          const items = data.items ?? [];

          if (items.length) {
            this.staffUser = items[0];
            this.cdr.markForCheck();
            this.loadSignature();
          }
        },
      });
  }

  private loadSignature() {
    if (this.staffUser?.id) {
      this.staffService
        .getSignature(this.staffUser.id)
        .pipe(
          catchError(err => {
            if (err) {
              console.error(err);
            }
            return of(null);
          }),
          takeUntil(this.destroy$),
        )
        .subscribe(image => {
          if (image?.size) {
            this.loadPictureFromBlob(image);
          }
        });
    } else {
      this.avatarUrl = avatar;
    }
  }

  private loadPictureFromBlob(blob: Blob | null) {
    if (!blob) {
      return;
    }

    if (blob.size > 102400) {
      this.confirmationService.info(`RegisterService::InvalidImageSize`, 'Image Upload', {
        cancelText: 'OK',
        hideYesBtn: true,
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      this.avatarUrl = reader.result as string;
      this.cdr.markForCheck();
    };
    reader.readAsDataURL(blob);
  }

  generatePin() {
    this.pin = this.tenancyService.generateRandomPassword(4, true);
  }

  onPinInput(value: string) {
    this.pin = value.replace(/\D/g, '').slice(0, 4);
  }

  generateKeyringPassword() {
    this.keyringPassword = this.tenancyService.generateRandomPassword(16, true, true, true, true);
    this.loadQrImage();
  }

  private loadQrImage(): void {
    if (!this.keyringPassword) {
      return;
    }

    this.staffService
      .getQRPictureUrl(this.keyringPassword)
      .pipe(takeUntil(this.destroy$))
      .subscribe((response: Blob) => {
        const reader = new FileReader();
        reader.onload = () => {
      this.qrImageSrc = reader.result ?? '';
      this.cdr.markForCheck();
    };
        reader.readAsDataURL(response);
      });
  }

  capitalizeFirstLetter(text: string): string {
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  onRoleClick(event: Event, name: string) {
    this.selectedRole = name;
    if (name !== 'manager') {
      this.isCostCentre = false;
    }

    const checked = (event.target as HTMLInputElement).checked;

    for (const roleGroup of this.roleGroups) {
      const val = roleGroup.value;
      roleGroup.setValue(
        { [Object.keys(val)[0]]: Object.keys(val)[0] === name && checked },
        { emitEvent: false },
      );
    }

    this.form?.get('roleNames')?.markAsTouched();
    this.form?.get('roleNames')?.updateValueAndValidity();
  }

  isFieldInvalid(name: string): boolean {
    const control = this.form?.get(name);
    return !!control && control.invalid;
  }

  getFieldErrorMessage(name: string): string {
    const control = this.form?.get(name);
    if (!control?.errors) {
      return '';
    }

    const label = UserModalComponent.fieldLabels[name] ?? name;

    if (control.errors['passwordRequiresLower']) {
      return `${label} must contain a lowercase letter.`;
    }
    if (control.errors['passwordRequiresUpper']) {
      return `${label} must contain an uppercase letter.`;
    }
    if (control.errors['passwordRequiresDigit']) {
      return `${label} must contain a digit.`;
    }
    if (control.errors['passwordRequiresNonAlphanumeric']) {
      return `${label} must contain a special character.`;
    }
    if (control.errors['required']) {
      return '';
    }
    if (control.errors['email']) {
      return 'Enter a valid email address.';
    }
    if (control.errors['pattern']) {
      return `${label} format is invalid.`;
    }
    if (control.errors['minlength']) {
      return `${label} must be at least ${control.errors['minlength'].requiredLength} characters.`;
    }
    if (control.errors['maxlength']) {
      return `${label} must be at most ${control.errors['maxlength'].requiredLength} characters.`;
    }

    return `${label} is invalid.`;
  }

  onVisibleChange(value: boolean) {
    this.visible = value;
    this.visibleChange.emit(value);
  }

  onSaveClicked() {
    if (!this.form || this.form.invalid || !this.isPinValid) return;

    const { id } = this.selected;
    const { roleNames } = this.form.value;
    const extraProperties = this.form.get('extraProperties')?.value ?? {};

    const mappedRoleNames: string[] =
      roleNames
        ?.filter((role: Record<string, boolean>) => !!role[Object.keys(role)[0]])
        ?.map((role: Record<string, boolean>) => Object.keys(role)[0]) || [];

    const payload = id
      ? {
          ...this.selected,
          ...this.form.value,
          roleNames: mappedRoleNames,
          organizationUnitIds: this.organization.checkedKeys,
          extraProperties: {
            ...extraProperties,
            notificationSettings: this.notificationSettings,
            pin: this.pin,
            keyringPassword: this.keyringPassword,
            restrictedLocations: this.isCostCentre ? [] : this.restrictedKeys,
            allowedLocations: this.isCostCentre ? [] : this.allowedKeys,
            costCentres: this.isCostCentre ? this.checkedKeys : [],
            restrictedEvents: this.restrictedEvents,
          },
        }
      : {
          ...this.form.value,
          roleNames: mappedRoleNames,
          organizationUnitIds: this.organization.checkedKeys,
          extraProperties: {
            ...extraProperties,
            notificationSettings: this.notificationSettings,
            pin: this.pin,
            keyringPassword: this.keyringPassword,
            restrictedLocations: this.isCostCentre ? [] : this.restrictedKeys,
            allowedLocations: this.isCostCentre ? [] : this.allowedKeys,
            costCentres: this.isCostCentre ? this.checkedKeys : [],
            restrictedEvents: this.restrictedEvents,
          },
        };

    this.saveClicked.emit(payload);
  }
}
